import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import {
  ActionSheetController,
  AlertController,
  IonModal,
  LoadingController,
  ModalController,
  ToastController,
} from '@ionic/angular';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { UpdateCaseDTO } from 'src/app/lost-found/case.dto';
import { Case } from 'src/app/lost-found/case.model';
import { CaseService } from 'src/app/lost-found/case.service';
import { ImageCropperComponent } from 'src/app/shared/image-cropper/image-cropper.component';
import { countrycodes } from 'src/app/shared/phone-codes/countrycodes';
import { PhoneCodesComponent } from 'src/app/shared/phone-codes/phone-codes.component';
import { AccountService } from '../../account.service';
import { OTPComponent } from 'src/app/shared/otp/otp.component';

@Component({
  selector: 'app-edit-case',
  templateUrl: './edit-case.page.html',
  styleUrls: ['./edit-case.page.scss'],
})
export class EditCasePage implements OnInit, OnDestroy {
  selectedCase: Case;
  caseType: string;
  updateCaseSub: Subscription;
  caseSub: Subscription;
  codeImage: string;
  selectedCode: string;
  selectedRegion = 'Lebanon';
  fullName: string;
  phoneNumber: string;
  region: string;
  details: string;
  userId: number;
  dogName: string;
  breed: string;
  age: number;
  medical: string;
  size: string = 'unknown';
  gender: string = 'unknown';
  images = [];
  selectedImage: string;
  hideTermsMessage = true;
  email: string;
  imagesCount: number;
  isLoading = true;
  oldPhoneNumber: string;
  @ViewChild('termsModal') termsModal: IonModal;
  constructor(
    private activatedRoute: ActivatedRoute,
    private caseService: CaseService,
    private modalCtrl: ModalController,
    private actionSheetCtrl: ActionSheetController,
    private loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private toastController: ToastController,
    private router: Router,
    private authService: AuthService,
    private accountService: AccountService
  ) {}

  async ngOnInit() {
    const caseId = +this.activatedRoute.snapshot.params.id;
    this.caseSub = this.caseService
      .getCaseById(caseId)
      .subscribe(async (res) => {
        this.selectedCase = res;
        this.caseType = this.selectedCase.type;
        this.userId = await this.authService.getUserId();
        this.fullName = this.selectedCase.fullName;
        this.email = this.selectedCase.email;
        this.phoneNumber = this.selectedCase.phoneNumber;
        this.selectedCode = this.selectedCase.phoneCode;
        this.selectedRegion = this.selectedCase.phoneRegion;
        this.codeImage = countrycodes.filter(
          (b) =>
            b.name.toLowerCase() === this.selectedRegion.toLowerCase() &&
            b.number === this.selectedCode
        )[0].flag;
        this.region = this.selectedCase.location;
        this.details = this.selectedCase.details;
        this.dogName = this.selectedCase.dogName;
        this.oldPhoneNumber = this.selectedCode + this.phoneNumber;
        this.breed = this.selectedCase.breed;
        this.age = this.selectedCase.age;
        this.medical = this.selectedCase.medical;
        this.size = this.selectedCase.size;
        this.gender = this.selectedCase.gender;
        this.images = [...this.selectedCase.images];
        this.images.forEach((image) => {
          image.isAdded = 0;
          image.isDeleted = 0;
        });
        this.imagesCount = this.images.length;
        this.isLoading = false;
      });
  }

  ngOnDestroy() {
    if (this.updateCaseSub) this.updateCaseSub.unsubscribe();
    if (this.caseSub) this.caseSub.unsubscribe();
  }

  changeCountryCode() {
    this.modalCtrl
      .create({
        component: PhoneCodesComponent,
      })
      .then((modalEl) => {
        modalEl.present();
        return modalEl.onDidDismiss();
      })
      .then((resData) => {
        if (resData.role === 'select') {
          const country = resData.data.country;
          this.selectedCode = country.number;
          this.selectedRegion = country.name;
          this.codeImage = country.flag;
        }
      });
  }

  cancel() {
    this.termsModal.dismiss();
  }

  save() {
    this.loadingCtrl
      .create({
        keyboardClose: true,
        showBackdrop: false,
        message: 'Please Wait...',
      })
      .then(async (loadingEl) => {
        loadingEl.present();
        const updateCaseDTO: UpdateCaseDTO = {
          id: this.selectedCase.id,
          details: this.details,
          email: this.email,
          location: this.region,
          phoneCode: this.selectedCode,
          phoneNumber: this.phoneNumber,
          phoneRegion: this.selectedRegion,
          type: this.caseType,
          images: [...this.images],
          userId: this.userId,
          age: this.age,
          breed: this.breed,
          dogName: this.dogName,
          gender: this.gender,
          medical: this.medical,
          size: this.size,
        };

        this.updateCaseSub = (
          await this.caseService.updateCase(updateCaseDTO)
        ).subscribe({
          next: async (updatedCase) => {
            const toast = await this.toastController.create({
              color: 'primary',
              duration: 2000,
              message: 'Case updated successfully',
            });
            loadingEl.dismiss();
            await toast.present();
            this.oldPhoneNumber = this.selectedCode + this.phoneNumber;
          },
          error: () => {
            loadingEl.dismiss();
            this.alertCtrl
              .create({
                header: 'An Error Occurred',
                message:
                  'Could not perform this action at the moment. Please try again later.',
                buttons: [{ text: 'Dismiss' }],
              })
              .then((alerEl) => {
                loadingEl.dismiss();
                alerEl.present();
              });
          },
        });
      });
  }

  addImage() {
    if (!Capacitor.isPluginAvailable('Camera')) {
      return;
    }
    this.actionSheetCtrl
      .create({
        buttons: [
          {
            text: 'Take Photo',
            icon: 'camera',
            handler: () => {
              this.getImage('Camera');
            },
            cssClass: 'actionsheetbutton',
          },
          {
            text: 'Choose from Gallery',
            icon: 'image',
            handler: () => {
              this.getImage('Gallery');
            },
            cssClass: 'actionsheetbutton',
          },
        ],
      })
      .then((actionSheetEl) => actionSheetEl.present());
  }

  getImage(from: string) {
    let source: any;
    if (from === 'Camera') {
      source = CameraSource.Camera;
    } else {
      source = CameraSource.Photos;
    }
    Camera.getPhoto({
      quality: 70,
      source,
      correctOrientation: true,
      resultType: CameraResultType.Base64,
      allowEditing: false,
    })
      .then((image) => {
        this.selectedImage = image.base64String;
        this.openImageCropper();
      })
      .catch((err) => {});
  }

  openImageCropper() {
    this.modalCtrl
      .create({
        component: ImageCropperComponent,
        componentProps: {
          uploadedImage: 'data:image/jpeg;base64, ' + this.selectedImage,
        },
      })
      .then((modalEl) => {
        modalEl.onDidDismiss().then((modalData) => {
          if (!modalData.data) {
            return;
          }
          var imageUrl = modalData.data['imageUrl'];
          const newImage = { id: -1, url: imageUrl, isDeleted: 0, isAdded: 1 };
          this.images.push(newImage);
          this.imagesCount++;
        });
        modalEl.present();
      });
  }

  removePhoto(index: number) {
    if (this.images[index].isAdded === 1) {
      this.images.splice(index, 1);
    } else {
      this.images[index].isDeleted = 1;
    }
    this.imagesCount--;
  }

  openTermsConditions() {
    this.modalCtrl
      .create({
        component: PhoneCodesComponent,
      })
      .then((modalEl) => {
        modalEl.present();
        return modalEl.onDidDismiss();
      })
      .then((resData) => {
        if (resData.role === 'select') {
          const country = resData.data.country;
          this.selectedCode = country.number;
          this.selectedRegion = country.name;
          this.codeImage = country.flag;
        }
      });
  }

  openOTP() {
    const isNumberValid = this.accountService.checkPhoneNumber(
      this.selectedCode + this.phoneNumber,
      this.selectedRegion
    );
    if (!isNumberValid) {
      this.alertCtrl
        .create({
          header: 'Invalid Phone Number',
          message: 'Make sure your phone number is correct then try again!',
          buttons: [{ text: 'Dismiss' }],
        })
        .then((alertEl) => alertEl.present());
    } else {
      this.modalCtrl
        .create({
          component: OTPComponent,
          componentProps: { phoneNumber: this.selectedCode + this.phoneNumber },
        })
        .then((modalEl) => {
          modalEl.onDidDismiss().then((modalData) => {
            if (!modalData.data) {
              return;
            } else {
              this.save();
            }
          });
          modalEl.present();
        });
    }
  }

  CheckNumber() {
    if (this.oldPhoneNumber == this.selectedCode + this.phoneNumber) {
      this.save();
    } else {
      this.openOTP();
    }
  }
}
